/**
 * Colour, typeface and text size, as CSS variables on the root element.
 *
 * The app's colours already run through variable-driven Tailwind ramps, so an
 * accent is a ten-line variable swap rather than a rebuild or a second
 * stylesheet — and everything that reads `bg-brand-600` or `ring-brand-500`
 * follows automatically, including the ones nobody remembers to update.
 *
 * All three preferences live in `localStorage` rather than on the account:
 * they describe the screen this is being read on, not the person reading, and
 * syncing them would push a choice made on a laptop onto a shared meeting-room
 * display.
 */

const STEPS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900] as const;

/** An accent family, as `r g b` triples per ramp step. */
export interface Accent {
  key: string;
  label: string;
  /** The swatch colour — step 600, the one buttons are filled with. */
  swatch: string;
  ramp: Record<(typeof STEPS)[number], string>;
}

/**
 * Each family is anchored so that step 600 clears 4.5:1 against white, because
 * that is the step `btn-primary` fills with under white text. Teal and ember
 * are shifted a step darker than their stock scales for exactly that reason —
 * a stock teal-600 button label sits at about 2.9:1 and is unreadable.
 */
export const ACCENTS: readonly Accent[] = [
  {
    key: 'indigo',
    label: 'Indigo',
    swatch: '#4f46e5',
    ramp: {
      50: '238 242 255',
      100: '224 231 255',
      200: '199 210 254',
      300: '165 180 252',
      400: '129 140 248',
      500: '99 102 241',
      600: '79 70 229',
      700: '67 56 202',
      800: '55 48 163',
      900: '49 46 129',
    },
  },
  {
    key: 'navy',
    label: 'Navy',
    swatch: '#23407f',
    /* The wordmark's own navy, which is what the phone app defaults to. */
    ramp: {
      50: '238 242 250',
      100: '219 227 244',
      200: '185 200 232',
      300: '142 165 214',
      400: '92 121 187',
      500: '55 85 157',
      600: '35 64 127',
      700: '26 48 103',
      800: '18 35 79',
      900: '10 23 56',
    },
  },
  {
    key: 'teal',
    label: 'Teal',
    swatch: '#0f766e',
    ramp: {
      50: '240 253 250',
      100: '204 251 241',
      200: '153 246 228',
      300: '94 234 212',
      400: '45 212 191',
      500: '13 148 136',
      600: '15 118 110',
      700: '17 94 89',
      800: '19 78 74',
      900: '15 61 58',
    },
  },
  {
    key: 'violet',
    label: 'Violet',
    swatch: '#7c3aed',
    ramp: {
      50: '245 243 255',
      100: '237 233 254',
      200: '221 214 254',
      300: '196 181 253',
      400: '167 139 250',
      500: '139 92 246',
      600: '124 58 237',
      700: '109 40 217',
      800: '91 33 182',
      900: '76 29 149',
    },
  },
  {
    key: 'ember',
    label: 'Ember',
    swatch: '#c2410c',
    ramp: {
      50: '255 247 237',
      100: '255 237 213',
      200: '254 215 170',
      300: '253 186 116',
      400: '251 146 60',
      500: '234 88 12',
      600: '194 65 12',
      700: '154 52 18',
      800: '124 45 18',
      900: '96 31 12',
    },
  },
  {
    key: 'rose',
    label: 'Rose',
    swatch: '#be123c',
    ramp: {
      50: '255 241 242',
      100: '255 228 230',
      200: '254 205 211',
      300: '253 164 175',
      400: '251 113 133',
      500: '225 29 72',
      600: '190 18 60',
      700: '159 18 57',
      800: '136 19 55',
      900: '107 15 43',
    },
  },
];

export const DEFAULT_ACCENT = 'indigo';

export function accentByKey(key: string | null): Accent {
  return ACCENTS.find((entry) => entry.key === key) ?? ACCENTS[0];
}

/**
 * The typefaces on offer.
 *
 * Only Inter is fetched; the rest are already on the machine. A picker that
 * quietly pulled four more webfonts would cost every user the download to
 * serve the few who change it.
 */
export interface FontChoice {
  key: string;
  label: string;
  /** Written into `--app-font`, which sits in front of the fallback stack. */
  stack: string;
}

export const FONTS: readonly FontChoice[] = [
  { key: 'inter', label: 'Inter', stack: 'Inter' },
  { key: 'system', label: 'System', stack: 'system-ui' },
  { key: 'serif', label: 'Serif', stack: 'Georgia, "Times New Roman", serif' },
  {
    key: 'mono',
    label: 'Mono',
    stack: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  },
];

export const DEFAULT_FONT = 'inter';

export function fontByKey(key: string | null): FontChoice {
  return FONTS.find((entry) => entry.key === key) ?? FONTS[0];
}

/**
 * Text size, as the root font size every `rem` in the app is measured from.
 *
 * Because Tailwind sizes both type and spacing in `rem`, this scales the
 * layout with the text instead of cramming larger words into the same boxes.
 * The range stops at 1.15: past that the chat composer and the sidebar rail
 * start fighting each other on a laptop screen.
 */
export interface TextSize {
  key: string;
  label: string;
  scale: number;
}

export const TEXT_SIZES: readonly TextSize[] = [
  { key: 'sm', label: 'Small', scale: 0.92 },
  { key: 'md', label: 'Default', scale: 1 },
  { key: 'lg', label: 'Large', scale: 1.075 },
  { key: 'xl', label: 'Larger', scale: 1.15 },
];

export const DEFAULT_TEXT_SIZE = 'md';

export function textSizeByKey(key: string | null): TextSize {
  return TEXT_SIZES.find((entry) => entry.key === key) ?? TEXT_SIZES[1];
}

export const ACCENT_KEY = 'itsappy.accent';
export const FONT_KEY = 'itsappy.font';
export const TEXT_SIZE_KEY = 'itsappy.textSize';

export function applyAccent(key: string) {
  const accent = accentByKey(key);
  for (const step of STEPS) {
    document.documentElement.style.setProperty(`--brand-${step}`, accent.ramp[step]);
  }
}

export function applyFont(key: string) {
  document.documentElement.style.setProperty('--app-font', fontByKey(key).stack);
}

export function applyTextSize(key: string) {
  const { scale } = textSizeByKey(key);
  // Left unset at the default so the browser's own font-size setting still
  // wins for anyone who changed it — writing `16px` here would override it.
  document.documentElement.style.fontSize = scale === 1 ? '' : `${scale * 100}%`;
}

/**
 * Puts the stored preferences on the document.
 *
 * Called from `main.tsx` at module scope, which runs before React's first
 * render, so the app never paints in the default accent and then jumps.
 */
export function applyStoredAppearance() {
  applyAccent(localStorage.getItem(ACCENT_KEY) ?? DEFAULT_ACCENT);
  applyFont(localStorage.getItem(FONT_KEY) ?? DEFAULT_FONT);
  applyTextSize(localStorage.getItem(TEXT_SIZE_KEY) ?? DEFAULT_TEXT_SIZE);
}
